// components/PhoneNumbersSection.tsx
import React from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  FormControlLabel,
  Switch,
  IconButton,
  Tooltip,
  Checkbox,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import { CustomerPhone, ValidationErrors } from "../types/customer";
import { typography, elevation } from "../theme";

interface PhoneNumbersSectionProps {
  phones: CustomerPhone[];
  phoneValidationErrors: (string | undefined)[];
  validationErrors: ValidationErrors;
  hasNoPhoneChecked: boolean;
  disabled?: boolean;
  onPhonesChange: (phones: CustomerPhone[]) => void;
  onPhoneValidationErrorsChange: (errors: (string | undefined)[]) => void;
  onHasNoPhoneCheckedChange: (checked: boolean) => void;
}

export const PhoneNumbersSection: React.FC<PhoneNumbersSectionProps> = ({
  phones,
  phoneValidationErrors,
  validationErrors,
  hasNoPhoneChecked,
  disabled = false,
  onPhonesChange,
  onPhoneValidationErrorsChange,
  onHasNoPhoneCheckedChange,
}) => {
  const handleAddPhone = () => {
    const newPhones = [
      ...phones,
      {
        number: "",
        isPrimary: phones.length === 0,
        type: "Mobile",
        note: "",
      },
    ];
    onPhonesChange(newPhones);
    onPhoneValidationErrorsChange([...phoneValidationErrors, undefined]);
  };

  const handlePhoneChange = (idx: number, input: string) => {
    const newPhones = [...phones];
    let valid = false;
    let parsed = null;
    let detectedType = "Unknown";

    if (input) {
      parsed = parsePhoneNumberFromString(input, "GB");
      valid = parsed ? parsed.isValid() : false;
      if (parsed && valid) {
        detectedType = parsed.getType() || "Unknown";
      }
    }

    newPhones[idx] = {
      ...phones[idx],
      number: input,
      type: detectedType,
      note: phones[idx].note,
    };
    onPhonesChange(newPhones);

    const errors = [...phoneValidationErrors];
    errors[idx] = !input
      ? undefined
      : valid
      ? undefined
      : "Invalid phone number";
    onPhoneValidationErrorsChange(errors);
  };

  const handlePrimaryChange = (idx: number, checked: boolean) => {
    const newPhones = phones.map((p, i) => ({
      ...p,
      isPrimary: i === idx ? checked : false,
    }));
    onPhonesChange(newPhones);
  };

  const handleDeletePhone = (idx: number) => {
    onPhonesChange(phones.filter((_, i) => i !== idx));
    onPhoneValidationErrorsChange(
      phoneValidationErrors.filter((_, i) => i !== idx)
    );
  };

  return (
    <Box gridColumn={{ xs: "span 12", sm: "span 12" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 1,
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle1">Phone Numbers</Typography>
        <Tooltip title="Add Phone">
          <IconButton onClick={handleAddPhone} disabled={disabled}>
            <AddCircleOutlineIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {validationErrors.phones && (
        <Typography
          color="error"
          variant="caption"
          sx={{ display: "block", mb: 1 }}
        >
          {validationErrors.phones}
        </Typography>
      )}
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 2 }}
        elevation={elevation.paper}
      >
        {phones.length === 0 && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
          >
            <Typography variant="body2" color="text.secondary">
              No phone numbers added.
            </Typography>
            <FormControlLabel
              label="Customer has no phone number"
              control={
                <Checkbox
                  checked={hasNoPhoneChecked}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    onHasNoPhoneCheckedChange(e.target.checked)
                  }
                  disabled={disabled}
                />
              }
              sx={{ m: 0 }}
            />
          </Box>
        )}
        {phones.map((phone, idx) => {
          const error = phoneValidationErrors[idx];
          return (
            <Box
              key={idx}
              sx={{
                display: "flex",
                alignItems: "flex-start",
                mb: 2,
                gap: 2,
                flexWrap: "wrap",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  width: "100%",
                }}
              >
                <TextField
                  label="Phone Number"
                  value={phone.number}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handlePhoneChange(idx, e.target.value)
                  }
                  sx={{ flex: 1 }}
                  disabled={disabled}
                  error={!!error}
                  helperText={error}
                />
                <TextField
                  label="Type"
                  value={phone.type === "FIXED_LINE" ? "LAND LINE" : phone.type}
                  InputProps={{ readOnly: true }}
                  sx={{
                    width: 120,
                    "& .MuiInputBase-input": {
                      fontSize: typography.fontSize.medium,
                    },
                  }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={phone.isPrimary}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handlePrimaryChange(idx, e.target.checked)
                      }
                      color="primary"
                      disabled={disabled}
                    />
                  }
                  label="Primary"
                />
                <IconButton
                  aria-label="delete"
                  onClick={() => handleDeletePhone(idx)}
                  color="error"
                  disabled={disabled}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <TextField
                label="Notes"
                value={phone.note || ""}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const newPhones = [...phones];
                  newPhones[idx] = {
                    ...phone,
                    note: e.target.value.slice(0, 255),
                  };
                  onPhonesChange(newPhones);
                }}
                placeholder="Optional"
                multiline
                rows={2}
                sx={{ flex: 1, width: "100%" }}
                inputProps={{ maxLength: 255 }}
                disabled={disabled}
                error={!!validationErrors[`phones[${idx}].Note`]}
                helperText={validationErrors[`phones[${idx}].Note`]}
              />
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};
